import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TeamDetailsService } from '../../../my-teams/team-details/team-details.service';
import { TeamService } from '../../../_core/services/team.service';
import { ToastService } from '../../../_core/services/toast.service';
import { UserService } from '../../../_core/services/user.service';
import { SelectPlayersPage } from '../../../_shared/components/select-players/select-players.page';
import { Team } from '../../../_shared/models/team.model';
import { Season } from '../../models/season.model';
import { User } from '../../models/user.model';
import { AddPlayerComponent } from '../add-player/add-player.component';
import { SeasonSelectorService } from '../season-selector/season-selector.service';
import { TeamHelperService } from '../../../my-teams/team-helper.service';

@Component({
  standalone: false,
  selector: 'app-select-team-player',
  templateUrl: './select-team-player.component.html',
  styleUrls: ['./select-team-player.component.scss'],
})
export class SelectTeamPlayerComponent implements OnInit, OnDestroy {
  @Input() teamId: string;
  @Input() participantType = 'INDIVIDUAL';
  @Input() selectedPlayers: User[] = [];
  public ths = inject(TeamHelperService);

  team: Team;
  players: User[] = [];
  isLoading: boolean;
  season: Season;

  isOwner: boolean;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private modalController: ModalController,
    private teamService: TeamService,
    private teamDetailsService: TeamDetailsService,
    private toast: ToastService,
    private userService: UserService,
    private seasonSelectorService: SeasonSelectorService
  ) {}

  ngOnInit() {
    if (this.teamId) {
      this.season = this.seasonSelectorService.season;
      this.getTeamParticipants();
    } else {
      this.getMyPlayers();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTeamParticipants() {
    this.isLoading = true;
    this.teamDetailsService
      .getParticiapnts(this.teamId, this.season?._id, 'player')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: async (res) => {
          this.team = res.data;
          this.getTeamPlayers();
          this.isOwner = this.teamDetailsService.isOwner(this.team.owners);
        },
        error: () => this.close(),
      });
  }

  private getMyPlayers() {
    this.isLoading = true;
    this.userService
      .myProfile()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (res) => {
          this.players = res.data.associated_users;
        },
      });
  }

  async addPlayer() {
    if (this.teamId) {
      const modal = await this.modalController.create({
        component: SelectPlayersPage,
        presentingElement: await this.modalController.getTop(),
      });

      await modal.present();
      const { data } = await modal.onDidDismiss();

      if (data) {
        const participantObj = {
          person: data[0]._id,
          roles: ['player'],
        };
        this.teamService
          .addPlayer(this.team._id, participantObj)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            this.team = res.data;
            this.getTeamPlayers();
            this.toast.presentToast(res.message);
          });
      }
    } else {
      const modal = await this.modalController.create({
        component: AddPlayerComponent,
      });

      await modal.present();

      const { data } = await modal.onDidDismiss();
      if (data) this.players.unshift(data);
    }
  }

  public getInitials(teamName: string) {
    if (teamName) {
      const array = teamName.split(' ');
      if (array.length === 1) {
        return teamName.charAt(0);
      } else {
        return array[0].charAt(0) + array[1].charAt(0);
      }
    }
  }

  removePlayer(playerId) {
    this.teamService
      .removePlayer(this.team._id, playerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.team = res.data;
          this.toast.presentToast(res.message);
        },
      });
  }

  selectPlayer(ev, i) {
    if (this.participantType === 'INDIVIDUAL') {
      this.modalController.dismiss([this.players[i]]);
    } else if (this.participantType === 'PAIR') {
      if (ev.detail.checked) {
        this.selectedPlayers.push(this.players[i]);
      } else {
        const index = this.selectedPlayers.findIndex(
          (sp) => sp._id === this.players[i]._id
        );
        this.selectedPlayers.splice(index, 1);
      }

      if (this.selectedPlayers.length === 2) {
        this.modalController.dismiss(this.selectedPlayers);
      }
    }
  }

  async close() {
    await this.modalController.dismiss();
  }

  private getTeamPlayers() {
    this.players = this.team.participants.map((p) => p.person);
  }
}
